import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Mail, Send, Trash2, X } from "lucide-react";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import {
    EmailingRecipient,
    MailSend,
    RecipientKind,
    RecipientRef,
    deleteEmailingContact,
    getEmailingRecipient,
    setUserMarketingOptOut,
    updateEmailingContact,
} from "interfaces/emailing";
import { CategoryBadge, OfferBadge, StatusBadge, effectiveStatus, fieldLabel, formatDate, since } from "./parts";
import SendDetail from "./SendDetail";

/** Right-hand panel: one user or prospect, every email we sent them, and « Envoyer un email ». */
export default function RecipientDrawer({
    kind,
    id,
    refreshKey,
    onClose,
    onSend,
    onChanged,
}: {
    kind: RecipientKind;
    id: string;
    refreshKey: number;
    onClose: () => void;
    onSend: (r: RecipientRef) => void;
    onChanged: () => void;
}) {
    const [data, setData] = useState<{ recipient: EmailingRecipient; sends: MailSend[] } | null>(null);
    const [openSend, setOpenSend] = useState<string | null>(null);
    const [form, setForm] = useState({ givenName: "", familyName: "", company: "", note: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setData(null);
        getEmailingRecipient(kind, id)
            .then((d) => {
                setData(d);
                if (d.recipient.kind === "contact") {
                    const c = d.recipient;
                    setForm({ givenName: c.givenName ?? "", familyName: c.familyName ?? "", company: c.company ?? "", note: c.note ?? "" });
                }
            })
            .catch(() => setData(null));
    }, [kind, id, refreshKey]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && !openSend && onClose();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, openSend]);

    const r = data?.recipient;

    const toggleOptOut = async () => {
        if (!r || !data) return;
        const next = !r.marketingOptOut;
        if (r.kind === "user") await setUserMarketingOptOut(r._id, next);
        else await updateEmailingContact(r._id, { marketingOptOut: next });
        setData({ ...data, recipient: { ...r, marketingOptOut: next } as EmailingRecipient });
        onChanged();
    };

    const saveContact = async () => {
        if (!r || r.kind !== "contact" || !data) return;
        setSaving(true);
        const res = await updateEmailingContact(r._id, form);
        setSaving(false);
        if (res.contact) {
            setData({ ...data, recipient: { ...res.contact, emails: r.emails } });
            onChanged();
        }
    };

    const removeContact = async () => {
        if (!r || r.kind !== "contact") return;
        if (!window.confirm(`Retirer ${r.email} des prospects ?\nLes emails déjà envoyés ne seront plus rattachés à une fiche.`)) return;
        await deleteEmailingContact(r._id);
        onChanged();
        onClose();
    };

    const contactDirty =
        r?.kind === "contact" && (form.givenName !== (r.givenName ?? "") || form.familyName !== (r.familyName ?? "") || form.company !== (r.company ?? "") || form.note !== (r.note ?? ""));

    return (
        <>
            <div className="fixed inset-0 z-40 bg-ink/20" onClick={onClose} />
            <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-surface shadow-float">
                <div className="flex items-start justify-between gap-3 border-b border-border p-6">
                    <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{kind === "user" ? "Utilisateur" : "Prospect sans compte"}</p>
                        <h2 className="truncate text-base font-semibold text-ink">{r ? r.name || r.email : "Chargement…"}</h2>
                        {r && <p className="truncate text-sm text-ink-muted">{r.email}</p>}
                        {r?.company && <p className="truncate text-sm text-ink-muted">{r.company}</p>}
                    </div>
                    <button onClick={onClose} aria-label="Fermer" className="rounded-lg p-1.5 text-ink-muted hover:bg-surface-sunken hover:text-ink">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {r && data && (
                    <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            {r.kind === "user" ? (
                                <>
                                    <div>
                                        <p className="text-ink-muted">Offre</p>
                                        <OfferBadge user={r} />
                                    </div>
                                    <div>
                                        <p className="text-ink-muted">Dernière connexion</p>
                                        <p className="text-ink">{since(r.lastConnection)}</p>
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <p className="text-ink-muted">Ajouté le</p>
                                    <p className="text-ink">{formatDate(r.createdAt)}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-ink-muted">Emails envoyés</p>
                                <p className="text-ink">{r.emails.count}</p>
                            </div>
                            <div>
                                <p className="text-ink-muted">Dernier envoi</p>
                                <p className="text-ink">{formatDate(r.emails.lastSentAt)}</p>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button size="sm" onClick={() => onSend({ kind: r.kind, _id: r._id, email: r.email, name: r.name })}>
                                <Send className="h-4 w-4" />
                                Envoyer un email
                            </Button>
                            {r.kind === "user" && (
                                <Link to={`/bo/activity?user=${r._id}`} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border-strong px-3 text-sm font-medium text-ink transition-colors hover:bg-surface-sunken">
                                    <Activity className="h-4 w-4" />
                                    Son activité
                                </Link>
                            )}
                        </div>

                        {r.kind === "contact" && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={fieldLabel}>Prénom</label>
                                        <Input value={form.givenName} onChange={(e) => setForm({ ...form, givenName: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className={fieldLabel}>Nom</label>
                                        <Input value={form.familyName} onChange={(e) => setForm({ ...form, familyName: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className={fieldLabel}>Entreprise</label>
                                    <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                                </div>
                                <div>
                                    <label className={fieldLabel}>Note</label>
                                    <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Contexte, d'où vient ce contact…" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Button size="sm" variant="secondary" onClick={saveContact} disabled={!contactDirty || saving}>
                                        {saving ? "Enregistrement…" : "Enregistrer"}
                                    </Button>
                                    <button onClick={removeContact} className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-danger">
                                        <Trash2 className="h-3.5 w-3.5" />
                                        Retirer des prospects
                                    </button>
                                </div>
                            </div>
                        )}

                        <label className="flex items-start gap-2 rounded-lg bg-surface-sunken p-3 text-sm text-ink">
                            <input type="checkbox" checked={!r.marketingOptOut} onChange={toggleOptOut} className="mt-0.5 rounded-sm accent-accent" />
                            <span>
                                Reçoit les emails marketing
                                <span className="block text-xs text-ink-muted">
                                    {r.marketingOptOut ? "Désinscrit : seuls les emails transactionnels lui sont envoyés." : "Décocher si la personne a demandé à ne plus en recevoir."}
                                </span>
                            </span>
                        </label>

                        <div>
                            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">Historique des emails</p>
                            {data.sends.length === 0 ? (
                                <p className="flex items-center gap-2 text-sm text-ink-muted">
                                    <Mail className="h-4 w-4" />
                                    Aucun email envoyé.
                                </p>
                            ) : (
                                <ul className="space-y-2">
                                    {data.sends.map((s) => (
                                        <li key={s._id}>
                                            <button onClick={() => setOpenSend(s._id)} className="w-full rounded-lg border border-border p-3 text-left transition-colors hover:bg-surface-sunken">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="truncate text-sm font-medium text-ink">{s.templateName}</p>
                                                    <StatusBadge status={effectiveStatus(s)} />
                                                </div>
                                                <div className="mt-1 flex items-center gap-2 text-xs text-ink-muted">
                                                    <CategoryBadge category={s.category} />
                                                    <span className="truncate">
                                                        {formatDate(s.createdAt, true)}
                                                        {s.sentByEmail ? ` · par ${s.sentByEmail}` : ""}
                                                    </span>
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </aside>
            {openSend && <SendDetail id={openSend} onClose={() => setOpenSend(null)} />}
        </>
    );
}
